<template>
  <v-layout row wrap align-content-start fill-height class="pa-4">
    <v-flex xs12 class="page-head-container mb-3">
      <div class="title-container">
        <h1 class="headline font-weight-light">Gastro Article</h1>
        <div class="help-container">
          <v-icon
            class="help-icon"
            @click="showHelpDialog = true"
          >
            help_outline
          </v-icon>
          <span class="help-text" @click="showHelpDialog = true">使用說明</span>
        </div>
      </div>
    </v-flex>

    <v-flex xs12>
      <v-select
        label="模版名稱"
        :items="templates"
        item-text="template_name"
        item-value="id"
        v-model="selectedTemplate"
        class="font-weight-bold mb-3 template-select"
        hide-details
      ></v-select>
    </v-flex>

      <!-- Drawer and Content -->
      <v-flex xs12 class="mb-3">
        <div class="template-drawer">
          <div class="drawer-bar" @click="toggleDrawer">
            <span>模板庫 Library（點擊{{ drawerOpen ? '收合' : '展開' }}）</span>
            <v-icon
              :class="{ 'rotate-icon': drawerOpen }"
              class="ml-2 expand-icon"
            >
              {{ drawerOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </v-icon>
          </div>
          <v-expand-transition>
            <div
              v-if="drawerOpen"
              class="drawer-panel"
              @click.stop
            >
              <div class="drawer-inner">
                <div class="drawer-left">
                  <left-navigation-panel
                    :top-static-sections="topStaticSections"
                    :bottom-static-sections="bottomStaticSections"
                    :preface-templates="prefaceTemplates"
                    :finding-templates="findingTemplates"
                    :intervention-templates="interventionTemplates"
                    :selected-nav-key="selectedNavKey"
                    @select-static="selectStaticSection"
                    @select-preface="selectPreface"
                    @select-finding="selectFinding"
                    @select-intervention="selectIntervention"
                  />
                </div>
                <div class="drawer-right">
                  <template v-if="selectedNavKey">
                    <template v-if="!activeEditor">
                      <template-editor
                        :title="templateCardTitle"
                        :templates="currentTemplates"
                        :selected-nav-key="selectedNavKey"
                        :active-editor="activeEditor"
                        @update-template="handleTemplateUpdate"
                        @save="saveFindingOrInterventionTemplate"
                      />
                    </template>
                    <section-editor
                      v-else
                      :title="editorTitle"
                      v-model="activeText"
                      @save="saveTemplate"
                    />
                  </template>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </v-flex>

      <v-flex xs12 class="preview-report-container" ref="previewContainer">
        <div class="shared-container">
          <!-- 模板標籤區域 - 暫不提供，整段隱藏 -->
          <div class="template-tags-section" v-if="false">
            <div class="template-tags-content">
              <div class="template-tags-container">
                <span
                  v-for="tag in templateTags"
                  :key="tag.id"
                  class="template-tag"
                  @click="openTemplateEditor(tag)"
                >
                  {{ tag.name }}
                </span>
              </div>
            </div>
          </div>

           <div class="container-content">
             <div class="left-panel">
               <div class="panel-content">
                 <preview-card
                   :intervention-templates="interventionTemplates"
                   :selected-item-name="selectedItemName"
                   :selected-id="selectedId"
                   :template-type="templateType"
                   @refresh="refreshGeneratedReportText"
                 />
               </div>
             </div>
             <div class="right-panel">
               <div class="panel-content">
                 <report-card :diffs="processedDiffs" @refresh="refreshGeneratedReportText" />
               </div>
             </div>
           </div>
         </div>
        <div
          v-if="drawerOpen"
          class="content-overlay"
          @click="closeDrawer"
        ></div>
      </v-flex>

      <!-- 模板編輯彈出框 -->
      <v-dialog
        v-model="showTemplateEditor"
        max-width="800px"
        draggable
        @click:outside="closeTemplateEditor"
      >
        <v-card class="content-card" elevation="1">
          <v-card-title class="card-title d-flex align-center">
            <span class="flex-grow-1">{{ currentEditingTemplate.name }}</span>
            <v-btn
              color="primary"
              @click="saveTemplate"
              class="save-btn"
            >
              儲存模板
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="font-weight-bold">模板內容</div>
            <v-textarea
              :value="currentEditingTemplate.content"
              @input="updateTemplateContent"
              solo
              label="請在此輸入模板內容"
              class="editor-textarea"
              rows="8"
            ></v-textarea>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-snackbar
        v-model="snackbar"
        bottom
        :timeout="timeout"
        color="green"
      >
        成功儲存模板！
      </v-snackbar>

      <!-- 使用說明彈出框 -->
      <v-dialog
        v-model="showHelpDialog"
        max-width="600px"
        @click:outside="showHelpDialog = false"
      >
        <v-card class="help-card" elevation="2">
          <v-card-title class="help-card-title">
            <v-icon class="help-title-icon">help_outline</v-icon>
            使用說明
          </v-card-title>
          <v-card-text class="help-card-content">
            <div class="help-section">
              <h3 class="help-section-title">模板庫 Library / 模板編輯區</h3>
              <ol class="help-list">
                <li>依需求點選模板類型進行內容編輯。</li>
                <li>編輯後需要點擊【儲存模板】按鍵，才會進行儲存。</li>
                <li>注意事項：{ }內的文字不可修改；{ }、[ ] 括弧符號不可刪除。</li>
              </ol>
            </div>

            <div class="help-section">
              <h3 class="help-section-title">測試區</h3>
              <ol class="help-list">
                <li>點擊【+】符號，新增病灶、處置，可以進行模板測試。</li>
              </ol>
            </div>

            <div class="help-section">
              <h3 class="help-section-title">報告預覽區</h3>
              <ol class="help-list">
                <li>點擊【重新整理箭頭】符號，可以進行報告內容預覽。</li>
              </ol>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-layout>
</template>

<script>
import { templateService } from "@/services/template-db";
import { TemplateLocations } from "@/utils/template-locations";
import LeftNavigationPanel from './components/gastro-article/LeftNavigationPanel.vue';
import TemplateEditor from './components/gastro-article/TemplateEditor.vue';
import PreviewCard from './components/gastro-article/PreviewCard.vue';
import ReportCard from './components/gastro-article/ReportCard.vue';
import SectionEditor from './components/gastro-article/SectionEditor.vue';
import { diffWords } from 'diff';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'GastroArticle',
  components: {
    LeftNavigationPanel,
    TemplateEditor,
    PreviewCard,
    ReportCard,
    SectionEditor,
  },
  data() {
    return {
      singularTemplateText: '',
      pluralTemplateText: '',
      currentTemplates: [],  // 當前正在編輯的模板數組
      editorHeight: 300,
      activeEditor: null, // 指向目前編輯的靜態段落位置
      selectedNavKey: null, // highlight current selected nav item
      sectionTexts: {
        [TemplateLocations.SUGGESTION]: '這是預設的建議文字。',
        [TemplateLocations.OTHER]: 'Hill\'s classification: GEFV grade: Hiatal hernia (-).',
      },

      selectedTemplate: null,
      selectedFinding: null,
      selectedIntervention: null,
      selectedPreface: null,
      selectedItemName: null,
      selectedFindingId: null,
      activeTemplateData: null,
      templateType: 'finding',
      findings: [
        TemplateLocations.ESOPHAGUS,
        TemplateLocations.STOMACH,
        TemplateLocations.DUODENUM,
        TemplateLocations.UNASSIGNED,
      ],
      templates: [],
      prefaceTemplates: [],
      topStaticSections: [],
      bottomStaticSections: [
        { key: TemplateLocations.SUGGESTION, label: '建議' },
      ],
      findingTemplates: [
        {
          id: TemplateLocations.ESOPHAGUS,
          name: '食道',
          children: [],
        },
        {
          id: TemplateLocations.STOMACH,
          name: '胃',
          children: [],
        },
        {
          id: TemplateLocations.DUODENUM,
          name: '十二指腸',
          children: [],
        },
        {
          id: TemplateLocations.UNASSIGNED,
          name: '未指定位置',
          children: [],
        },
      ],
      interventionTemplates: [],
      snackbar: false,
      timeout: 3000,
      generatedReportText: '',
      previousText: '',
      doctorTemplateMasterId: null,
      drawerOpen: false,
      showTemplateEditor: false,
      showHelpDialog: false,
      currentEditingTemplate: {
        name: '',
        content: '',
      },
    }
  },
  computed: {
    ...mapGetters('doctors', ['getDoctors']),
    staticSections() {
      return [...this.topStaticSections, ...this.bottomStaticSections];
    },
    processedDiffs() {
      const diffs = diffWords(this.previousText, this.generatedReportText || '');
      const renderableParts = [];

      for (let i = 0; i < diffs.length; i++) {
        const part = diffs[i];
        const nextPart = diffs[i + 1];

        if (part.removed && nextPart && nextPart.added) {
          renderableParts.push({
            value: nextPart.value,
            isChanged: true,
            oldValue: part.value,
          });
          i++;
        } else if (part.added) {
          renderableParts.push({
            value: part.value,
            isChanged: true,
            oldValue: '',
          });
        } else if (!part.removed) {
          renderableParts.push({
            value: part.value,
            isChanged: false,
          });
        }
      }
      return renderableParts;
    },
    selectedId() {
      if (this.selectedFinding) {
        return this.selectedFinding.id;
      }
      if (this.selectedIntervention) {
        return this.selectedIntervention.id;
      }
      return null;
    },
    templateCardTitle() {
      let title = '編輯模板';
      if (this.selectedItemName) {
        title += ` - ${this.selectedItemName}`;
      }
      return title;
    },
    editorTitle() {
      if (this.selectedPreface) {
        return `編輯前言 - ${this.selectedPreface.name}`
      }
      if (this.activeEditor === TemplateLocations.SUGGESTION) {
        return '編輯建議'
      }
      if (this.activeEditor === TemplateLocations.OTHER) {
        return '編輯其他模板'
      }
      return ''
    },
    activeText: {
      get() {
        if (!this.activeEditor) {
          return ''
        }
        return this.sectionTexts[this.activeEditor] || ''
      },
      set(value) {
        if (!this.activeEditor) {
          return
        }
        this.$set(this.sectionTexts, this.activeEditor, value)
      },
    },
    // 模板標籤列表
    templateTags() {
      const tags = [];

      // 靜態段落（前言 / 建議 / 其他模板）永遠存在
      this.staticSections.forEach(section => {
        tags.push({
          id: section.key,
          name: section.label,
          type: 'section',
          content: this.sectionTexts[section.key] || ''
        });
      });

      // 根據選擇的模板類型添加對應標籤
      if (this.templateType === 'finding' && this.selectedItemName) {
        tags.push({
          id: 'finding',
          name: this.selectedItemName,
          type: 'finding',
          content: this.getFindingContent()
        });
      } else if (this.templateType === 'intervention' && this.selectedItemName) {
        tags.push({
          id: 'intervention',
          name: this.selectedItemName,
          type: 'intervention',
          content: this.getInterventionContent()
        });
      }

      // 添加沒有病灶的預設模板標籤
      tags.push(
        {
          id: 'esophagus-negative',
          name: '食道-Negative',
          type: 'negative',
          content: this.getNegativeContent('esophagus')
        },
        {
          id: 'stomach-negative',
          name: '胃-Negative',
          type: 'negative',
          content: this.getNegativeContent('stomach')
        },
        {
          id: 'duodenum-negative',
          name: '十二指腸-Negative',
          type: 'negative',
          content: this.getNegativeContent('duodenum')
        }
      );

      return tags;
    },
  },
  async mounted() {
    window.addEventListener('resize', this.calculateEditorHeight)
    window.addEventListener('resize', this.updatePreviewHeight)
    await this.ensureDoctorsLoaded();
    await this.fetchTemplates()
    this.refreshGeneratedReportText();
    // Hide browser vertical scrollbar while this view is active
    this.toggleNoVertScroll(true);
    // Ensure preview/report area fits within viewport height
    this.$nextTick(() => this.updatePreviewHeight())
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateEditorHeight)
    window.removeEventListener('resize', this.updatePreviewHeight)
    // Restore scrollbar when leaving this view
    this.toggleNoVertScroll(false);
  },
  activated() {
    // Re-apply when using keep-alive
    this.toggleNoVertScroll(true);
  },
  deactivated() {
    // Remove when deactivated via keep-alive
    this.toggleNoVertScroll(false);
  },
  methods: {
    ...mapActions('doctors', ['fetchDoctors']),
    async ensureDoctorsLoaded() {
      try {
        if (!Array.isArray(this.getDoctors) || this.getDoctors.length === 0) {
          await this.fetchDoctors();
        }
      } catch (error) {
        console.error('Failed to load doctors list:', error);
      }
    },
    resolveDoctorTemplateMasterId() {
      if (!this.selectedTemplate || !Array.isArray(this.templates)) {
        return null;
      }
      const selectedTemplateRecord = this.templates.find(
        template => String(template.id) === String(this.selectedTemplate),
      );
      if (!selectedTemplateRecord) {
        return null;
      }
      const templateName = (selectedTemplateRecord.template_name || '').trim();
      if (templateName) {
        const doctors = Array.isArray(this.getDoctors) ? this.getDoctors : [];
        const matchedDoctor = doctors.find(
          doctor => (doctor.name || '').trim() === templateName,
        );
        if (matchedDoctor) {
          const rawDoctorId = matchedDoctor.template_master_id ?? matchedDoctor.templateMasterId;
          const parsedDoctorId = Number(rawDoctorId);
          if (!Number.isNaN(parsedDoctorId)) {
            return parsedDoctorId;
          }
        }
      }
      const fallbackRawId = selectedTemplateRecord.template_master_id ?? selectedTemplateRecord.id;
      const fallbackParsedId = Number(fallbackRawId);
      return Number.isNaN(fallbackParsedId) ? null : fallbackParsedId;
    },
    applyDoctorTemplateMasterId() {
      const resolvedId = this.resolveDoctorTemplateMasterId();
      if (resolvedId !== this.doctorTemplateMasterId) {
        this.doctorTemplateMasterId = resolvedId;
        return true;
      }
      return false;
    },
    transI18n(str) {
      return str;
    },
    // Toggle global class to hide/show vertical scrollbar
    toggleNoVertScroll(on) {
      const cls = 'no-vert-scroll';
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle(cls, !!on);
        document.body && document.body.classList.toggle(cls, !!on);
      }
    },
    // Dynamically size the preview/report container to fit in viewport
    updatePreviewHeight() {
      this.$nextTick(() => {
        const el = this.$refs.previewContainer;
        if (!el || typeof window === 'undefined') return;
        const rect = el.getBoundingClientRect();
        const bottomPadding = 24; // keep a fixed gap from screen bottom
        const available = Math.max(240, Math.floor(window.innerHeight - rect.top - bottomPadding));
        el.style.height = available + 'px';
      });
    },
    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen;
      // 如果展開模板庫，自動選中前言的第一個模板
      if (this.drawerOpen) {
        if (this.prefaceTemplates.length > 0) {
          this.selectPreface(this.prefaceTemplates[0], this.makeKey('preface', this.prefaceTemplates[0].name));
        } else {
          const defaultSection = this.topStaticSections[0] || this.staticSections[0];
          if (defaultSection) {
            this.selectStaticSection(defaultSection);
          }
        }
      }
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    syncBottomStaticSections(hasOtherTemplate) {
      const sections = [];
      if (hasOtherTemplate) {
        sections.push({ key: TemplateLocations.OTHER, label: '其他模版' });
      }
      sections.push({ key: TemplateLocations.SUGGESTION, label: '建議' });
      const shouldResetSelection = !hasOtherTemplate && this.selectedNavKey === TemplateLocations.OTHER;
      this.bottomStaticSections = sections;
      if (shouldResetSelection) {
        const fallbackSection = sections[0];
        if (fallbackSection) {
          this.selectStaticSection(fallbackSection);
        } else {
          this.selectedNavKey = null;
          this.setActiveEditor(null);
        }
      }
    },
    refreshGeneratedReportText(isSwitching = false) {
      const options = {};
      if (this.doctorTemplateMasterId !== null) {
        options.templateMasterId = this.doctorTemplateMasterId;
      }
      templateService.generateText(6, options)
          .then(response => {
            if (response && response.data) {
              if (isSwitching) {
                this.previousText = response.data.text;
              } else {
                this.previousText = this.generatedReportText;
              }
              this.generatedReportText = response.data.text;
            }
          })
    },
    makeKey(type, name, parent = null) {
      // Normalize to a stable selection key for nav highlighting
      const norm = s => String(s || '').trim()
      const t = norm(type)
      const n = norm(name)
      const p = norm(parent)
      return p ? `${t}:${p}:${n}` : `${t}:${n}`
    },
    fetchTemplates() {
      return templateService.getTemplateNames().then(res => {
        const templates = res
        this.templates = templates
        if (templates.length > 0) {
          this.selectedTemplate = templates[0].id
        }
      })
    },
    async fetchTemplateDetails(id) {
      try {
        const res = await templateService.getTemplate(id);
        this.activeTemplateData = res;
        const templateMasterId = res.id;
        const templateMap = new Map(res.templates.map(t => [t.id, t]));
        const hasOtherTemplate = res.templates.some(t => t.location === TemplateLocations.OTHER);
        this.syncBottomStaticSections(hasOtherTemplate);
        const prefaceTemplateRecords = res.templates
          .filter(t => t.location === 'Preface')
          .sort((a, b) => a.id - b.id);
        const hasMultiplePrefaceTemplates = prefaceTemplateRecords.length > 1;
        const staticLocations = this.staticSections.map(section => section.key);
        staticLocations.forEach(location => {
          const matchedTemplate = res.templates.find(t => t.location === location);
          if (matchedTemplate) {
            this.$set(this.sectionTexts, location, matchedTemplate.content);
          }
        });

        let resolvedPrefaceTemplates = [];
        if (hasMultiplePrefaceTemplates) {
          const prefaceMappingsRes = await templateService.getFindingTemplateMappings({
            templatePurpose: 'preface',
            templateMasterId,
          });
          const prefaceMappings = (prefaceMappingsRes && prefaceMappingsRes.success && Array.isArray(prefaceMappingsRes.data))
            ? prefaceMappingsRes.data
            : [];
          if (prefaceMappings.length > 0) {
            resolvedPrefaceTemplates = prefaceMappings.map((mapping, index) => {
              const templateRecord = templateMap.get(mapping.template_id) || {};
              return {
                id: templateRecord.id || mapping.template_id,
                name: `模板${index + 1}`,
                content: templateRecord.content || '',
                mappingId: mapping.id,
                priority: mapping.priority,
                templateMasterId: mapping.template_master_id,
                templateType: mapping.template_type || 'single',
              };
            });
          }
        }

        if (resolvedPrefaceTemplates.length === 0 && prefaceTemplateRecords.length > 0) {
          resolvedPrefaceTemplates = prefaceTemplateRecords.map((templateRecord, index) => ({
            id: templateRecord.id,
            name: prefaceTemplateRecords.length === 1 ? '模板一' : `模板${index + 1}`,
            content: templateRecord.content || '',
            mappingId: null,
            priority: index + 1,
            templateMasterId,
            templateType: templateRecord.template_type || 'single',
          }));
        }

        this.prefaceTemplates = resolvedPrefaceTemplates;

        this.findings.forEach((template) => {
          const foundTemplate = res.templates.find(t => t.location === template);
          if (foundTemplate) {
            const findingTemplate = this.findingTemplates.find(t => t.id === template);

            if (findingTemplate) {
              const templatesForLocation = res.templates.filter(t => t.location === template);
              const findingsMap = new Map();

              for (const t of templatesForLocation) {
                // 將 null 的 finding_text 正規化為 'Negative'
                const nameText = (t.finding_text == null) ? 'Negative' : t.finding_text
                if (!findingsMap.has(nameText)) {
                  findingsMap.set(nameText, {
                    id: t.finding_id,
                    name: nameText,
                    singular: null,
                    plural: null,
                    singleTemplateId: null,
                    pluralTemplateId: null,
                  });
                }
                const entry = findingsMap.get(nameText);
                if (t.template_type === 'single') {
                  entry.singular = t.content;
                  entry.singleTemplateId = t.id;
                }
                if (t.template_type === 'plural') {
                  entry.plural = t.content;
                  entry.pluralTemplateId = t.id;
                }
              }

              findingTemplate.children = Array.from(findingsMap.values()).map(entry => {
                entry.plural = entry.plural;
                entry.singular = entry.singular;
                return entry;
              });
            }
          }
        })

        const interventionTemplatesMap = new Map();
        const interventions = res.templates.filter(t => t.intervention_id !== null);

        for (const t of interventions) {
          if (!interventionTemplatesMap.has(t.intervention_text)) {
            interventionTemplatesMap.set(t.intervention_text, {
              id: t.intervention_id,
              name: t.intervention_text,
              templates: [],  // 儲存所有相關模板（包括mapping的）
              defaultSingleTemplate: null,  // 預設單數模板
              defaultPluralTemplate: null,  // 預設複數模板
            });
          }
          const entry = interventionTemplatesMap.get(t.intervention_text);
          // 先收集預設模板
          if (t.template_type === 'single') {
            if (!entry.defaultSingleTemplate) {
              entry.defaultSingleTemplate = { id: t.id, content: t.content };
            }
          }
          if (t.template_type === 'plural') {
            if (!entry.defaultPluralTemplate) {
              entry.defaultPluralTemplate = { id: t.id, content: t.content };
            }
          }
        }

        // 獲取每個 intervention 的 finding_template_mapping
        this.interventionTemplates = await Promise.all(
          Array.from(interventionTemplatesMap.values()).map(async (entry) => {
            try {
              const mappingsRes = await templateService.getFindingTemplateMappings({
                interventionId: entry.id,
                templatePurpose: 'intervention',
                templateMasterId,
              });
              if (mappingsRes && mappingsRes.success && mappingsRes.data && mappingsRes.data.length > 0) {
                const mappings = mappingsRes.data;
                entry.templates = mappings.map(mapping => {
                  const templateRecord = templateMap.get(mapping.template_id) || {};
                  return {
                    id: mapping.id,
                    templateId: mapping.template_id,
                    templateType: mapping.template_type || 'single',
                    priority: mapping.priority,
                    description: mapping.description,
                    content: templateRecord.content || '',
                    templateMasterId: mapping.template_master_id,
                  };
                });
                // 補上預設複數模板（如果 mapping 中沒有 plural 模板）
                const hasPlural = entry.templates.some(t => t.templateType === 'plural');
                if (!hasPlural && entry.defaultPluralTemplate) {
                  entry.templates.push({
                    templateId: entry.defaultPluralTemplate.id,
                    templateType: 'plural',
                    priority: 999,
                    description: '預設複數模板',
                    content: entry.defaultPluralTemplate.content,
                    templateMasterId: templateMasterId,
                  });
                }
              } else {
                // 如果沒有 mapping 資料，使用原設計：單數模板 + 複數模板
                entry.templates = [];
                if (entry.defaultSingleTemplate) {
                  entry.templates.push({
                    templateId: entry.defaultSingleTemplate.id,
                    templateType: 'single',
                    priority: 999,
                    description: null,  // 不顯示額外說明
                    content: entry.defaultSingleTemplate.content,
                  });
                }
                if (entry.defaultPluralTemplate) {
                  entry.templates.push({
                    templateId: entry.defaultPluralTemplate.id,
                    templateType: 'plural',
                    priority: 999,
                    description: null,  // 不顯示額外說明
                    content: entry.defaultPluralTemplate.content,
                  });
                }
              }

            } catch (err) {
              console.error(`Error fetching mappings for intervention ${entry.id}:`, err);
              // 發生錯誤時使用原設計：單數模板 + 複數模板
              entry.templates = [];
              if (entry.defaultSingleTemplate) {
                entry.templates.push({
                  templateId: entry.defaultSingleTemplate.id,
                  templateType: 'single',
                  priority: 999,
                  description: null,  // 不顯示額外說明
                  content: entry.defaultSingleTemplate.content,
                });
              }
              if (entry.defaultPluralTemplate) {
                entry.templates.push({
                  templateId: entry.defaultPluralTemplate.id,
                  templateType: 'plural',
                  priority: 999,
                  description: null,  // 不顯示額外說明
                  content: entry.defaultPluralTemplate.content,
                });
              }
            }

            return entry;
          })
        );
      } catch (err) {
        console.error('Error fetching template details:', err);
      }
    },
    selectStaticSection(section) {
      if (!section) {
        return;
      }
      const key = typeof section === 'string' ? section : section.key;
      const matched = typeof section === 'string'
        ? this.staticSections.find(item => item.key === key)
        : section;
      if (!matched) {
        return;
      }
      this.templateType = 'static';
      this.selectedNavKey = matched.key;
      this.setActiveEditor(matched.key);
      this.selectedItemName = matched.label;
      this.selectedFinding = null;
      this.selectedIntervention = null;
      this.selectedPreface = null;
      this.selectedFindingId = null;
    },
    selectPreface(preface, key) {
      this.templateType = 'preface';
      this.selectedNavKey = key;
      this.selectedPreface = preface;
      this.selectedFinding = null;
      this.selectedIntervention = null;
      this.selectedItemName = preface.name;
      this.selectedFindingId = null;
      this.$set(this.sectionTexts, preface.id, preface.content || '');
      this.setActiveEditor(preface.id);
    },
    selectFinding(finding, key) {
      const isSwitching = this.templateType === 'intervention';
      this.templateType = 'finding';
      this.selectedNavKey = key;
      this.selectedFinding = finding;
      this.selectedIntervention = null;
      this.selectedPreface = null;
      this.selectedItemName = finding.name;
      this.selectedFindingId = (finding && finding.id) ? finding.id : null;
      this.setActiveEditor(null);
      this.singularTemplateText = finding.singular || '';
      this.pluralTemplateText = finding.plural || '';

      // 設置 currentTemplates 以顯示單數/複數模板編輯器
      this.currentTemplates = [];
      if (finding.singleTemplateId) {
        this.currentTemplates.push({
          templateId: finding.singleTemplateId,
          templateType: 'single',
          content: finding.singular || '',
        });
      }
      if (finding.pluralTemplateId) {
        this.currentTemplates.push({
          templateId: finding.pluralTemplateId,
          templateType: 'plural',
          content: finding.plural || '',
        });
      }

      this.refreshGeneratedReportText(isSwitching);
    },
    selectIntervention(intervention, key) {
      const isSwitching = this.templateType === 'finding';
      this.templateType = 'intervention';
      this.selectedNavKey = key;
      this.selectedFinding = null;
      this.selectedIntervention = intervention;
      this.selectedPreface = null;
      this.selectedItemName = intervention.name;
      this.selectedFindingId = null;
      this.setActiveEditor(null);

      // 使用新的 templates 數組結構
      this.currentTemplates = intervention.templates ? [...intervention.templates] : [];

      const hasMapping = true;
      const singleTemplates = this.currentTemplates.filter(t => t.templateType === 'single');
      const pluralTemplates = this.currentTemplates.filter(t => t.templateType === 'plural');

      if (hasMapping) {
        // 有 mapping：顯示所有單數模板（標記為模板一、二…）並保留複數模板供編輯
        this.currentTemplates = [...singleTemplates, ...pluralTemplates];
      } else {
        // 沒有 mapping：僅保留預設單數模板與複數模板
        const defaultSingle = singleTemplates.length > 0 ? singleTemplates[0] : null;
        this.currentTemplates = [
          ...(defaultSingle ? [defaultSingle] : []),
          ...pluralTemplates,
        ];
      }

      this.refreshGeneratedReportText(isSwitching);
    },
    handleTemplateUpdate({ index, content }) {
      if (this.currentTemplates[index]) {
        this.currentTemplates[index].content = content;

        // 如果是 finding 類型，同步更新 singularTemplateText 和 pluralTemplateText
        if (this.templateType === 'finding') {
          const template = this.currentTemplates[index];
          if (template.templateType === 'single') {
            this.singularTemplateText = content;
          } else if (template.templateType === 'plural') {
            this.pluralTemplateText = content;
          }
        }
      }
    },
    setActiveEditor(editor) {
      this.activeEditor = editor
      if (editor) {
        this.calculateEditorHeight()
      }
    },
    calculateEditorHeight() {
      this.$nextTick(() => {
        if (this.$refs.editorTextarea) {
          const textareaElement = this.$refs.editorTextarea.$el
          const top = textareaElement.getBoundingClientRect().top
          const newHeight = window.innerHeight - top - 48 // 48px for bottom margin/padding
          this.editorHeight = newHeight > 100 ? newHeight : 100 // Min height
        }
      })
    },
    saveTemplate() {
      if (!this.activeTemplateData || !this.activeEditor) return;

      let current;

      if (this.selectedPreface) {
        current = this.activeTemplateData.templates.find(t => t.id === this.activeEditor);
      } else {
        current = this.activeTemplateData.templates.find(t => t.location === this.activeEditor);
      }

      const newContent = this.sectionTexts[this.activeEditor] || '';

      if (!current) {
        const payload = {
          template_master_id: this.activeTemplateData.id,
          location: this.selectedPreface ? 'Preface' : this.activeEditor,
          template_type: 'single',
          content: newContent,
          finding_id: null,
          intervention_id: null,
        };
        templateService.createTemplate(payload)
          .then(created => {
            if (created) {
              this.activeTemplateData.templates.push(created);
            }
            this.snackbar = true;
          })
          .catch(err => {
            console.error('Error creating template:', err);
            alert('新增模板失敗。');
          });
        return;
      }

      templateService.updateTemplate(current.id, { content: newContent })
        .then(() => {
          if (this.selectedPreface) {
            this.selectedPreface.content = newContent;
          }
          this.snackbar = true;
        })
        .catch(err => {
          console.error('Error saving template:', err);
          alert('Failed to save template.');
        });
    },
    saveFindingOrInterventionTemplate() {
      if (!this.activeTemplateData || !this.selectedNavKey) return;

      const keyParts = this.selectedNavKey.split(':');
      const type = keyParts[0]; // 'finding' or 'intervention'

      const promises = [];

      if (type === 'intervention') {
        // 使用新的 currentTemplates 數組保存所有模板
        for (const template of this.currentTemplates) {
          if (template.templateId) {
            // 從 activeTemplateData 中找到原始內容進行比較
            const originalTemplate = this.activeTemplateData.templates.find(t => t.id === template.templateId);
            const originalContent = originalTemplate ? originalTemplate.content : '';

            if (template.content !== originalContent) {
              promises.push(
                templateService.updateTemplate(template.templateId, { content: template.content })
              );
            }
          }
        }
      } else if (type === 'finding') {
        // Finding 邏輯保持不變（暫時）
        const singleId = this.selectedFinding && this.selectedFinding.singleTemplateId;
        const pluralId = this.selectedFinding && this.selectedFinding.pluralTemplateId;
        if (!singleId && !pluralId) {
          alert('錯誤：找不到要更新的模板（缺少 template.id）。');
          return;
        }
        if (singleId !== null && singleId !== undefined && this.singularTemplateText !== this.selectedFinding.singular) {
          promises.push(templateService.updateTemplate(singleId, { content: this.singularTemplateText }));
        }
        if (pluralId !== null && pluralId !== undefined && this.pluralTemplateText !== this.selectedFinding.plural) {
          promises.push(templateService.updateTemplate(pluralId, { content: this.pluralTemplateText }));
        }
      }

      if (promises.length > 0) {
        Promise.all(promises).then(() => {
          this.snackbar = true;

          // 更新 intervention templates 的內容
          if (this.selectedIntervention && type === 'intervention') {
            const isBiopsyIntervention = Number(this.selectedIntervention.id) === 53;
            if (isBiopsyIntervention) {
              const originalTemplates = this.selectedIntervention.templates || [];
              const updatedTemplates = originalTemplates.map(template => {
                const matched = this.currentTemplates.find(t => t.templateId === template.templateId);
                if (matched) {
                  return { ...template, content: matched.content };
                }
                return template;
              });
              this.selectedIntervention.templates = updatedTemplates;
            } else {
              this.selectedIntervention.templates = [...this.currentTemplates];
            }
          }

          // 更新 finding 的內容
          if (this.selectedFinding && type === 'finding') {
            this.selectedFinding.singular = this.singularTemplateText;
            this.selectedFinding.plural = this.pluralTemplateText;
          }

          this.refreshGeneratedReportText();
        }).catch(err => {
          console.error('Error saving template:', err);
          alert('儲存模板失敗。');
        });
      } else {
        console.log('沒有模板內容變更，無需儲存。');
      }
    },
    openTemplateEditor(template) {
      this.currentEditingTemplate = {
        name: template.name,
        content: template.content,
      };
      this.showTemplateEditor = true;
    },
    closeTemplateEditor() {
      this.showTemplateEditor = false;
      this.currentEditingTemplate = {
        name: '',
        content: '',
      };
    },
    updateTemplateContent(content) {
      this.currentEditingTemplate.content = content;
    },
    // 獲取各種模板內容的方法
    getFindingContent() {
      // 這裡可以從 store 或 props 獲取病灶內容
      return '這是病灶模板內容...';
    },
    getInterventionContent() {
      // 這裡可以從 store 或 props 獲取介入治療內容
      return '這是介入治療模板內容...';
    },
    getNegativeContent(organ) {
      // 這裡可以從 store 或 props 獲取無病灶內容
      const organNames = {
        esophagus: '食道',
        stomach: '胃',
        duodenum: '十二指腸'
      };
      return `${organNames[organ]}檢查未發現異常...`;
    },
  },
  watch: {
    selectedTemplate(newId) {
      if (newId) {
        const hasChanged = this.applyDoctorTemplateMasterId();
        if (hasChanged) {
          this.previousText = this.generatedReportText;
          this.refreshGeneratedReportText();
        }
        this.fetchTemplateDetails(newId);
      }
    },
    getDoctors: {
      handler() {
        const hasChanged = this.applyDoctorTemplateMasterId();
        if (hasChanged) {
          this.previousText = this.generatedReportText;
          this.refreshGeneratedReportText();
        }
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.page-head-container {
  margin-bottom: 8px !important;
}

.headline {
  color: #2f3542;
  padding-bottom: 8px;
  border-bottom: 1px solid #e6e9ef;
  letter-spacing: 0.2px;
}

.template-select >>> .v-input__slot {
  border-radius: 8px;
}

.template-drawer {
  width: 100%;
  position: relative;
}

.drawer-bar {
  background-color: #ffffff; /* 改回白色背景 */
  border: 1px solid #eef1f6; /* 改回原來的邊框色 */
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06); /* 改回原來的陰影 */
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #2f3542; /* 改回原來的文字顏色 */
}

.expand-icon {
  transition: transform 0.3s ease;
  color: #666; /* 改回原來的圖示顏色 */
}

.rotate-icon {
  transform: rotate(180deg);
}

.drawer-panel {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 8px;
  max-height: 66vh;
  overflow-y: auto;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06);
  z-index: 2000;
  transition: all 0.3s ease-in-out; /* 添加滑順的過渡效果 */
}

.drawer-inner {
  display: flex;
}

.drawer-left {
  flex: 0 0 30%;
  padding-right: 16px;
}

.drawer-right {
  flex: 0 0 70%;
}

.preview-report-container {
  position: relative;
  height: calc(100vh - 200px); /* 初始值，將由腳本覆蓋 */
  box-sizing: border-box; /* 確保高度包含內邊距，避免超出視窗 */
}

.shared-container {
  background: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(18, 38, 63, 0.08);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* allow children to shrink/scroll inside flex container */
  min-height: 0;
}

.container-content {
  display: flex;
  flex: 1;
  padding: 12px 12px 20px 12px; /* 增加底部內邊距 */
  /* let inner panels calculate height correctly in flexbox */
  min-height: 0;
}

.left-panel, .right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  /* important for nested flex scrolling */
  min-height: 0;
}

.panel-content {
  flex: 1;
  padding: 12px;
  /* make inner card size to available height */
  display: flex;
  flex-direction: column;
  min-height: 0; /* critical for flex children to shrink/scroll */
}

/* Let cards inside panel-content use all available height */
.panel-content > .content-card {
  flex: 1 1 auto;
  min-height: 0;
}

.content-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1500;
}

/* 彈出框樣式 - 與編輯模板區保持一致 */
.content-card {
  border-radius: 10px;
  background-color: #ffffff;
  border: 1px solid #eef1f6;
  box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06);
}

.card-title {
  font-size: 1.08rem;
  font-weight: 600;
  color: #2f3542;
  padding: 14px 16px 6px;
}

.v-card__text {
  padding: 8px 16px 16px;
  color: #4a4f57;
  line-height: 1.6;
}

.editor-textarea.v-textarea .v-input__control {
  background-color: #fff;
}

.editor-textarea >>> .v-input__slot {
  border-radius: 10px;
  border: 1px solid #e6e9ef;
}

.save-btn {
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
}

/* 標題容器樣式 */
.title-container {
  display: flex;
  align-items: center;
}

.help-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 8px;
}

.help-icon {
  font-size: 18px;
  color: #666;
  margin-right: 4px;
  transition: color 0.2s ease;
}

.help-text {
  font-size: 14px;
  color: #666;
  transition: color 0.2s ease;
}

.help-container:hover .help-icon,
.help-container:hover .help-text {
  color: #333;
}

/* 使用說明彈出框樣式 */
.help-card {
  border-radius: 12px;
}

.help-card-title {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2f3542;
}

.help-title-icon {
  color: #007bff;
}

.help-card-content {
  padding: 20px;
}

.help-section {
  margin-bottom: 20px;
}

.help-section:last-child {
  margin-bottom: 0;
}

.help-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2f3542;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 2px solid #e9ecef;
}

.help-list {
  margin: 0;
  padding-left: 20px;
  color: #4a4f57;
  line-height: 1.6;
}

.help-list li {
  margin-bottom: 6px;
}

.help-list li:last-child {
  margin-bottom: 0;
}

/* 模板標籤樣式 */
.template-tags-section {
  padding: 23px 30px 0px 30px;
  background-color: #ffffff;
}

.template-tags-content {
  display: flex;
  align-items: center;
}

.template-tags-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin-right: 12px;
  white-space: nowrap;
}

.template-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.template-tag {
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  padding: 4px 12px;
  border: 1px solid #ddd;
  border-radius: 16px;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.template-tag:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}
</style>

<style>
html.no-vert-scroll,
body.no-vert-scroll {
  height: 100vh;
  overflow-y: hidden;
  /* Hide scrollbar cross-browser */
  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none;    /* Firefox */
}
body.no-vert-scroll::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}
</style>
